import { useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import { categories, comics, getCategoryName } from '@/mocks/comics';

type SortKey = 'latest' | 'rating' | 'hot';

export default function Category() {
  const [params] = useSearchParams();
  const catKey = params.get('cat') || '';
  const searchQ = params.get('q') || '';
  const [sort, setSort] = useState<SortKey>('latest');

  const catName = getCategoryName(catKey);

  const list = useMemo(() => {
    let arr = catKey ? comics.filter((c) => c.categoryKey === catKey) : [...comics];
    if (searchQ) {
      const q = searchQ.toLowerCase();
      arr = arr.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.author.toLowerCase().includes(q) ||
          c.tags.some((t) => t.includes(searchQ)) ||
          c.category.includes(searchQ)
      );
    }
    if (sort === 'rating') arr = [...arr].sort((a, b) => b.rating - a.rating);
    else if (sort === 'hot') arr = [...arr].sort((a, b) => parseFloat(b.views) - parseFloat(a.views));
    return arr;
  }, [catKey, searchQ, sort]);

  const sortTabs: { key: SortKey; name: string }[] = [
    { key: 'latest', name: '最新上架' },
    { key: 'rating', name: '高分优先' },
    { key: 'hot', name: '人气最高' },
  ];

  const pageTitle = searchQ
    ? `搜索"${searchQ}"结果 - 韩国漫画网 | 韩漫星球`
    : `${catName} - 韩国漫画网${catKey ? ` ${catName}分类` : '漫画大全'} | 韩漫星球`;

  const pageDesc = searchQ
    ? `搜索"${searchQ}"相关的韩国漫画共${list.length}部，涵盖爱情、奇幻、动作、校园、悬疑等分类。`
    : catKey
    ? `浏览${catName}分类下的海量韩国漫画，精选优质${catName}，高清在线畅读，每日更新。`
    : `浏览韩国漫画网全部漫画分类，爱情、奇幻、动作、校园、悬疑等全部分类一网打尽，海量漫画按类型快速筛选。`;

  return (
    <>
      <Seo
        title={pageTitle}
        description={pageDesc}
        keywords="韩国漫画网,漫画分类,漫画大全,漫画在线阅读"
        canonicalPath={catKey ? `/category?cat=${catKey}` : '/category'}
        type="website"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin : '/' },
              { '@type': 'ListItem', position: 2, name: catKey ? catName : '全部漫画', item: typeof window !== 'undefined' ? window.location.href : '/category' },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: catKey ? `${catName}合集` : '全部漫画合集',
            description: pageDesc,
            mainEntity: {
              '@type': 'ItemList',
              itemListElement: list.map((c, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                name: c.title,
                url: typeof window !== 'undefined' ? `${window.location.origin}/comic/${c.id}` : `/comic/${c.id}`,
              })),
            },
          },
        ]}
      />

      <Navbar />

      <main className="pt-20 md:pt-24">
        <section className="border-b border-background-200 bg-background-50">
          <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-10">
            <nav aria-label="面包屑" className="text-sm text-foreground-500">
              <ol className="flex flex-wrap items-center gap-1">
                <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-foreground-800">{searchQ ? '搜索' : catKey ? catName : '全部漫画'}</li>
              </ol>
            </nav>
            <h1 className="mt-3 font-heading text-2xl md:text-3xl text-foreground-900">
              {searchQ ? `搜索"${searchQ}"` : catKey ? catName : '全部漫画'}
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-foreground-500">
              {searchQ
                ? `为你找到 ${list.length} 部相关漫画，快去看看吧。`
                : catKey
                ? `${categories.find((c) => c.key === catKey)?.desc || ''}`
                : '爱情、奇幻、动作、校园、悬疑等全部分类一网打尽，海量漫画按类型快速筛选。'}
            </p>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <div className="inline-flex rounded-full border border-background-300 bg-background-100 p-1">
                {sortTabs.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setSort(t.key)}
                    className={`rounded-full px-4 py-1.5 text-sm transition-colors whitespace-nowrap ${
                      sort === t.key ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-primary-600'
                    }`}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
              <span className="text-xs text-foreground-400">共 {list.length} 部作品</span>
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-8 md:py-12">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            {list.length > 0 ? (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {list.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-search-eye-line text-3xl"></i>
                </span>
                <h2 className="mt-4 font-heading text-xl text-foreground-800">没有找到相关漫画</h2>
                <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或浏览全部分类。</p>
                <Link
                  to="/category"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap"
                >
                  浏览全部漫画
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}